import { YOUTUBE_API_URL } from '../constants/blogger';
import type { YouTubeSearchResponse, YouTubeVideoItem } from '../types/youtube';

/**
 * Singleton manager for YouTube API operations (channel videos, search, tags).
 */
export class YoutubeManager {
    private static instance: YoutubeManager;

    /** Cached result of fetchChannelVideoTagCounts(); only fetched from API when empty. */
    private cachedTagCounts: { tag: string; count: number }[] | null = null;

    /** All videos loaded with paging; fetchYouTubeVideos only calls the endpoint when this is empty. */
    private allVideos: YouTubeVideoItem[] = [];

    private constructor() {}

    static getInstance(): YoutubeManager {
        if (!YoutubeManager.instance) {
            YoutubeManager.instance = new YoutubeManager();
        }
        return YoutubeManager.instance;
    }

    /**
     * Fetches YouTube videos from the API and returns normalized video items.
     * Loads all channel videos with paging and caches them in allVideos.
     * Only calls the endpoint when allVideos is empty; otherwise returns allVideos.slice(0, count).
     */
    async fetchYouTubeVideos(count: number, orderField: string = 'date'): Promise<YouTubeVideoItem[]> {
        if (this.allVideos.length > 0) {
            return this.allVideos.slice(0, count);
        }

        const maxResults = 50;
        const collected: YouTubeVideoItem[] = [];
        let pageToken: string | undefined;
        let globalIndex = 0;

        do {
            let url = `${YOUTUBE_API_URL}${encodeURIComponent(`&maxResults=${maxResults}&order=${orderField}`)}`;
            if (pageToken) {
                url += `${encodeURIComponent(`&pageToken=${pageToken}`)}`;
            }
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error('Failed to fetch videos');
            }
            const data: YouTubeSearchResponse = await response.json();
            const items = (data.items ?? []).filter(
                (item) => item.id?.kind === 'youtube#video' && item.id?.videoId
            );
            for (const item of items) {
                const thumbnails = item.snippet?.thumbnails;
                const thumbnail =
                    thumbnails?.high?.url ??
                    thumbnails?.medium?.url ??
                    thumbnails?.default?.url ??
                    '';
                collected.push({
                    number: ++globalIndex,
                    videoId: item.id.videoId!,
                    title: item.snippet?.title ?? '',
                    thumbnail,
                    description: item.snippet?.description ?? '',
                    publishedAt: item.snippet?.publishedAt,
                    channelTitle: item.snippet?.channelTitle ?? ''
                });
            }
            pageToken = data.nextPageToken;
        } while (pageToken);

        collected.sort((a, b) => {
            const aTime = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
            const bTime = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
            return bTime - aTime;
        });

        this.allVideos = collected.map((item, index) => ({
            ...item,
            number: index + 1
        }));
        return this.allVideos.slice(0, count);
    }

    /**
     * Search YouTube videos with optional filters (date range, free text, keywords).
     * Uses fetchYouTubeVideos to get all videos and filters in memory; does not call the search endpoint.
     */
    async searchYouTubeVideos(
        filters: {
            freeText?: string;
            keywords?: string;
            dateStart?: string;
            dateEnd?: string;
        },
        maxResults: number = 24
    ): Promise<YouTubeVideoItem[]> {
        const all = await this.fetchYouTubeVideos(100000, 'date');
        const dateStartMs = filters.dateStart
            ? new Date(filters.dateStart).getTime()
            : null;
        const dateEndMs = filters.dateEnd
            ? new Date(filters.dateEnd + 'T23:59:59.999Z').getTime()
            : null;
        const freeText = filters.freeText?.trim().toLowerCase() ?? '';
        const keywords = filters.keywords?.trim()
            ? filters.keywords.split(',').map((k) => k.trim().toLowerCase()).filter(Boolean)
            : [];

        const filtered = all.filter((item) => {
            if (dateStartMs != null && item.publishedAt) {
                const t = new Date(item.publishedAt).getTime();
                if (t < dateStartMs) return false;
            }
            if (dateEndMs != null && item.publishedAt) {
                const t = new Date(item.publishedAt).getTime();
                if (t > dateEndMs) return false;
            }
            const title = (item.title ?? '').toLowerCase();
            const desc = (item.description ?? '').toLowerCase();
            const combined = title + ' ' + desc;
            if (freeText && !combined.includes(freeText)) return false;
            if (keywords.length > 0) {
                const hasAll = keywords.every((kw) => combined.includes(kw));
                if (!hasAll) return false;
            }
            return true;
        });

        return filtered.slice(0, maxResults).map((item, index) => ({
            ...item,
            number: index + 1
        }));
    }

    /**
     * Fetches tags from all channel videos with usage counts (for tag cloud).
     * Results are cached; the REST endpoint is only called when the cache is empty.
     */
    async fetchChannelVideoTagCounts(): Promise<{ tag: string; count: number }[]> {
        if (this.cachedTagCounts !== null) {
            return this.cachedTagCounts;
        }

        const videoIds = await this.getAllVideoIds();
        if (videoIds.length === 0) {
            this.cachedTagCounts = [];
            return [];
        }

        const chunkSize = 50;
        const countByTag = new Map<string, number>();

        for (let i = 0; i < videoIds.length; i += chunkSize) {
            const chunk = videoIds.slice(i, i + chunkSize);
            const idsParam = chunk.join(',');
            //const videosUrl = `https://www.googleapis.com/youtube/v3/videos?key=${YOUTUBE_API_KEY}&id=${idsParam}&part=snippet`;
            const videosUrl = `${YOUTUBE_API_URL}${encodeURIComponent(`&id=${idsParam}`)}`;
            const videosRes = await fetch(videosUrl);
            if (!videosRes.ok) throw new Error('Failed to fetch video details');
            const videosData = await videosRes.json();
            const videoItems = videosData.items ?? [];
            for (const v of videoItems) {
                const tags = v.snippet?.tags;
                if (Array.isArray(tags)) {
                    tags.forEach((t: string) => {
                        const tag = typeof t === 'string' ? t.trim() : '';
                        if (tag) countByTag.set(tag, (countByTag.get(tag) ?? 0) + 1);
                    });
                }
            }
        }

        const result = Array.from(countByTag.entries())
            .map(([tag, count]) => ({ tag, count }))
            .sort((a, b) => b.count - a.count);
        this.cachedTagCounts = result;
        return result;
    }

    /**
     * Returns all video IDs from the channel.
     * Uses fetchYouTubeVideos to get all videos (no direct API call); returns their videoId values.
     */
    async getAllVideoIds(): Promise<string[]> {
        const videos = await this.fetchYouTubeVideos(100000, 'date');
        return videos.map((v) => v.videoId);
    }
}
